import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { UpdateOrganizationDocument } from '@graphql/types/documents';
import { useCallback } from 'react';

import type { UpdateOrganizationInput } from '../types';

export const useUpdateOrg = () => {
  const [updateOrgMutation, { loading }] = useMutation(UpdateOrganizationDocument);

  const doUpdateOrg = useCallback(
    async (orgId: string, input: UpdateOrganizationInput) => {
      const response = await updateOrgMutation({
        variables: {
          updateOrganizationId: orgId,
          input,
        },
      });

      const organizationRes = response.data?.updateOrganization;

      if (!organizationRes) {
        notify.error({ title: 'Organization update failed', description: 'No response from server' });
        throw new Error('Organiztion update failed');
      }

      // Check for errors in the response - show error toast if there are errors
      if (organizationRes.errors && organizationRes.errors.length > 0) {
        const errorMessage =
          organizationRes.errors[0]?.message || 'Failed to update organization. Please try again.';

        notify.error({ title: 'Organization update failed', description: errorMessage });

        return organizationRes;
      }

      // Show success toast only if the organization was updated successfully (no errors)
      if (organizationRes.organization) {
        notify.success({ title: 'Organization updated successfully' });
      }

      return organizationRes;
    },
    [updateOrgMutation]
  );

  return { doUpdateOrg, updateOrgLoading: loading };
};
